import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import GlobalStyles from '@mui/material/GlobalStyles';
import {
  createTheme,
  ThemeProvider,
} from '@mui/material/styles';
import {
  typeFaces,
  fontSizes,
  lineHeights,
  letterSpacing,
} from './constants';
import colors from './colors';
import breakpoints from './breakpoints';

const themeColors = {
  primary: colors.primary,
  primaryFallback: colors.primaryFallback,
  secondary: colors.secondary,
  primaryText: colors.primaryText,
  blackHigh: colors.blackHigh,
  blackMedium: colors.blackMedium,
  blackInactive: colors.blackInactive,
  blackDisabled: colors.blackDisabled,
  black: colors.black,
  blackGradient: colors.blackGradient,
  greyDark: colors.greyDark,
  greyLight: colors.greyLight,
  whiteLight: colors.whiteLight,
  white: colors.white,
  blue: colors.blue,
  blueDark: colors.blueDark,
} as const;

const themeFonts = {
  primary: typeFaces.primary,
  secondary: typeFaces.secondary,
  article: typeFaces.article,
  system: typeFaces.system,
} as const;

const themeSizes = {
  h1: fontSizes.headline1,
  h2: fontSizes.headline2,
  h3: fontSizes.headline3,
  h4: fontSizes.headline4,
  h5: fontSizes.headline5,
  h6: fontSizes.headline6,
  body1: fontSizes.body1,
  body2: fontSizes.body2,
  caption: fontSizes.caption,
  button: fontSizes.button,
  subtitle1: fontSizes.subtitle1,
  subtitle2: fontSizes.subtitle2,
  overline: fontSizes.overline,
} as const;

export const theme = createTheme({
  colors: themeColors,
  fonts: themeFonts,
  sizes: themeSizes,
  breakpoints: {
    values: {
      xs: breakpoints.xs,
      sm: breakpoints.sm,
      md: breakpoints.md,
      lg: breakpoints.lg,
      xl: breakpoints.xl,
    },
  },
  palette: {
    contrastThreshold: 4.5,
    primary: {
      main: themeColors.primary,
    },
    primaryFallback: {
      main: themeColors.primaryFallback,
    },
    secondary: {
      main: themeColors.secondary,
    },
    text: {
      primary: themeColors.blackHigh,
      secondary: themeColors.blackMedium,
      disabled: themeColors.blackDisabled,
    },
  },
  typography: {
    fontFamily: typeFaces.primary,
    h1: {
      fontFamily: themeFonts.secondary,
      color: themeColors.blackHigh,
      fontSize: themeSizes.h1,
      lineHeight: lineHeights.headline1,
      letterSpacing: letterSpacing.headline1,
      fontWeight: 'bold',
    },
    h2: {
      fontFamily: themeFonts.secondary,
      color: themeColors.blackHigh,
      fontSize: themeSizes.h2,
      lineHeight: lineHeights.headline2,
      letterSpacing: letterSpacing.headline2,
      fontWeight: 'bold',
    },
    h3: {
      fontFamily: themeFonts.secondary,
      color: themeColors.blackHigh,
      fontSize: themeSizes.h4,
      lineHeight: lineHeights.headline4,
      letterSpacing: letterSpacing.headline4,
      '@media(min-width: 480px)': {
        fontSize: themeSizes.h3,
        lineHeight: lineHeights.headline3,
        letterSpacing: letterSpacing.headline3,
      },
      fontWeight: 'bold',
    },
    h4: {
      fontFamily: themeFonts.secondary,
      color: themeColors.blackHigh,
      fontSize: themeSizes.h4,
      lineHeight: lineHeights.headline4,
      letterSpacing: letterSpacing.headline4,
      fontWeight: 'bold',
    },
    h5: {
      fontFamily: themeFonts.secondary,
      color: themeColors.blackHigh,
      fontSize: themeSizes.h5,
      lineHeight: lineHeights.headline5,
      letterSpacing: letterSpacing.headline5,
      fontWeight: 'bold',
    },
    h6: {
      fontFamily: themeFonts.secondary,
      color: themeColors.blackInactive,
      fontSize: themeSizes.h6,
      lineHeight: lineHeights.headline6,
      letterSpacing: letterSpacing.headline6,
      fontWeight: 'bold',
    },
    subtitle1: {
      fontFamily: themeFonts.secondary,
      color: themeColors.blackHigh,
      fontSize: themeSizes.subtitle1,
      lineHeight: lineHeights.subtitle1,
      letterSpacing: letterSpacing.subtitle1,
      fontWeight: 'bold',
    },
    subtitle2: {
      fontFamily: themeFonts.secondary,
      color: themeColors.blackInactive,
      fontSize: themeSizes.subtitle2,
      lineHeight: lineHeights.subtitle2,
      letterSpacing: letterSpacing.subtitle2,
      fontWeight: 'bold',
    },
    body1: {
      fontFamily: themeFonts.primary,
      color: themeColors.blackHigh,
      fontSize: themeSizes.body1,
      lineHeight: lineHeights.body1,
      letterSpacing: letterSpacing.body1,
    },
    body2: {
      fontFamily: themeFonts.primary,
      color: themeColors.blackMedium,
      fontSize: themeSizes.body2,
      lineHeight: lineHeights.body2,
      letterSpacing: letterSpacing.body2,
    },
    button: {
      fontFamily: themeFonts.secondary,
      fontSize: themeSizes.button,
      lineHeight: lineHeights.button,
      letterSpacing: letterSpacing.button,
      textTransform: 'uppercase',
      fontWeight: 'bold',
    },
    caption: {
      fontFamily: themeFonts.secondary,
      color: themeColors.blackInactive,
      fontSize: themeSizes.caption,
      lineHeight: lineHeights.caption,
      letterSpacing: letterSpacing.caption,
      fontWeight: 'bold',
    },
    overline: {
      fontFamily: themeFonts.secondary,
      color: themeColors.primary,
      fontSize: themeSizes.overline,
      lineHeight: lineHeights.overline,
      letterSpacing: letterSpacing.overline,
      fontWeight: 'bold',
    },
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      styleOverrides: {
        containedInfo: {
          color: themeColors.white,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          paddingTop: '24px',
          paddingBottom: '24px',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          listStyleType: 'none',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 0,
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: themeColors.white,
          color: themeColors.blackMedium,
          maxWidth: '600px',
          flexWrap: 'nowrap',
          borderRadius: 0,
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          maxWidth: '800px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: themeFonts.primary,
          margin: '4px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '@media (min-width: 600px)': {
            minWidth: '0px',
            fontSize: '1.125rem',
            padding: '0px 16px',
            marginTop: '4px',
          },
        },
        textColorInherit: {
          color: themeColors.blackHigh,
          opacity: '1',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: themeFonts.secondary,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: themeColors.blackMedium,
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        formControl: {
          fontFamily: themeFonts.secondary,
          color: themeColors.blackMedium,
        },
      },
    },
  },
});

export const hyphenation = {
  hyphens: 'auto',
  hyphenateLimitChars: '14 5 5',
} as const;

const globalStyles = (
  <GlobalStyles styles={() => ({
    ':root': {
      ...hyphenation,
      overflowY: 'scroll',
    },
  })}
  />
);

const themePropertyTypes = {
  children: PropTypes.node,
};

type ThemeProperties = InferProps<typeof themePropertyTypes>;

export default function ThemeWrapper({ children }: ThemeProperties) {
  return (
    <ThemeProvider theme={theme}>
      {globalStyles}
      {children}
    </ThemeProvider>
  );
}

ThemeWrapper.propTypes = themePropertyTypes;
